// The ticket detail modal arrives on first need, not in the boot entry.
//
// TicketDetailModal.js (89 KB minified) rode the tickets EAGER slice only
// because the shell root bare-keys `TicketsDetailModal` (app.js) and smbls used
// to merge a parked `if:` element's extends at parse time. Element >= 3.14.798
// re-merges a parked element on its first true `if:` when its name was
// registered since (FW-IF-GATED-ELEMENT-IGNORES-LATE-REGISTERED-EXTENDS-1), so:
//
//   need (a seeded `selectedTicket`, the /ticket/:id full page, or a
//   `?ticket=` deep link) -> load the module once -> add its components to the
//   live registry (add-missing) -> `root._ticketModalReady = true` -> the latch
//   opens and the modal builds with its full component.
//
// The predicate reads `_ticketModalReady` on every pass, so the write that
// ends the load re-runs it. A /tickets route that already folded the full
// tickets context registers the component first; then no load happens.
//
// Frank note (FA204): the in-flight load lives on a global holder, not a
// module-scope binding, and every helper below is self-contained. The default
// loader is written in the body, not as a default parameter over a module
// const: frank publishes a default parameter that reads the body's own scope
// alias before it exists, so a call without `loader` threw on publication.

// A module's components: its PascalCase object exports (functions such as
// `copyTicketUrl` are not components).
export const ticketDetailModalComponents = (mod) => {
  const out = {}
  for (const k in mod || {}) {
    const v = mod[k]
    if (/^[A-Z]/.test(k) && v && typeof v === 'object') out[k] = v
  }
  return out
}

export const ensureTicketDetailModal = (el, root, loader) => {
  const load =
    typeof loader === 'function'
      ? loader
      : () => import('@symbo-ls/workspace-tickets/components/TicketDetailModal.js')
  if (!root || typeof root.update !== 'function') return null
  const holder = (globalThis.__ticketDetailModalLoad = globalThis.__ticketDetailModalLoad || {})
  const components = el && el.context && el.context.components
  const markReady = () => {
    if (!root._ticketModalReady) root.update({ _ticketModalReady: true }, { preventFetch: true })
  }
  if (components && (components.TicketsDetailModal || components['smbls.TicketsDetailModal'])) {
    // Already registered (a tickets route folded the full context). Mark ready
    // outside the predicate's own effect run.
    Promise.resolve().then(markReady)
    return null
  }
  if (holder.pending) return holder.pending
  holder.pending = Promise.resolve()
    .then(() => load())
    .then((mod) => {
      const comps = (function (m) {
        const out = {}
        for (const k in m || {}) {
          const v = m[k]
          if (/^[A-Z]/.test(k) && v && typeof v === 'object') out[k] = v
        }
        return out
      })(mod && mod.default && !mod.TicketsDetailModal ? mod.default : mod)
      const reg = el && el.context && el.context.components
      if (reg) {
        for (const k in comps) if (!reg[k]) reg[k] = comps[k]
      }
      markReady()
    })
    .catch((err) => {
      console.warn('[ticketDetailModal] load failed:', err && err.message)
    })
    .finally(() => {
      holder.pending = null
    })
  return holder.pending
}

// A `?ticket=<id>` deep link in the current URL.
export const ticketParamInUrl = () => {
  if (typeof window === 'undefined') return false
  try {
    return !!new URL(window.location.href).searchParams.get('ticket')
  } catch (_) {
    return false
  }
}

// The need/ready latch behind the shell root's `if:` for TicketsDetailModal.
// One-way: once built, the modal's own `hide:` owns visibility
// (WS-SHELL-MOUNT-TREE-BUILD-2700MS-1). `inUrl` and `loader` are arguments,
// never closed over: frank publishes the `if:` as a standalone string, and a
// free factory parameter is a ReferenceError there (the published predicate
// threw on `loader`, so a ticket opened before the modal module loaded never
// mounted it; `ticketParamInUrl` resolved to nothing, so a `?ticket=` deep
// link never asked for it).
export const ticketDetailModalNeed = (el, s, inUrl, loader) => {
  const root = s && s.root
  // Read first, every pass: the load's write must re-run this predicate.
  const ready = !!(root && root._ticketModalReady)
  if (el.__wsDeferredMounted) return true
  const need =
    !!root &&
    (root.selectedTicket != null ||
      !!root._ticketFullPage ||
      !!(typeof inUrl === 'function' && inUrl()))
  if (!need) return false
  if (!ready) {
    ensureTicketDetailModal(el, root, loader)
    return false
  }
  el.__wsDeferredMounted = true
  return true
}

// The shell root's `if:` (app.js) — self-contained, so it publishes as is.
export const needTicketDetailModal = (el, s) => ticketDetailModalNeed(el, s, ticketParamInUrl)

// The same latch with injected inputs, for tests.
export const makeNeedTicketDetailModal =
  ({ ticketParamInUrl: inUrl, loader } = {}) =>
  (el, s) =>
    ticketDetailModalNeed(el, s, inUrl, loader)
